// Third-party
import React, { useState, useEffect } from 'react';
import { SortingRule } from 'react-table';
import { connect } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import union from 'lodash/union';
import classNames from 'classnames';

// Components
import { SecondaryButton, PrimaryButton } from '../../buttons';
import { ModelsPortfoliosList } from '../modal-content';
import { CloseButton } from '../../buttons';
import Modal from '@/views/components/modals/AccessibleModal';

// Constants and types
import { MODAL_SIZES } from '@/utils/constants';
import UserPreferences from '@/types/user-preferences';
import ThemeMode from '@/types/theme-mode';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '@/utils/3rd-party/heap';
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { PERMISSIONS, userHasPermission, userHasPermissions } from '@/utils/permissions';
import { isEnterpriseModel } from '@/utils/portfolio/utils';

// Enums
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '../../../../enums/Portfolio';
import { modelType } from '@/types/data';

// Redux
import { ReduxState } from '../../../../redux/modules/types';
import { getModelsPortfolios } from '../../../../redux/modules/portfolios';
import { selectIsLoadingEvery } from '../../../../redux/modules/ui/loaders';
import { ADD_PORTFOLIOS_TO_COMPARISON } from '../../../../redux/modules/comparison';
import Portfolio from '@/types/portfolio';
import EnterpriseDetails from '@/types/enterprise-detail-info';
import User from '@/types/user';
import PaginatedPortfolios from '@/types/paginated-portfolios';

type StateProps = {
  isComparing?: boolean;
  portfolios: Portfolio[];
  myPortfolios: Portfolio[];
  enterprisePortfolios: Portfolio[];
  enterprise: EnterpriseDetails;
  nextModelsToken?: string;
  nextMyModelsToken?: string;
  nextEnterpriseModelsToken?: string;
  userPreferences?: UserPreferences;
};

type DispatchProps = {
  getModelsPortfolios: (type?: modelType, query?: string, sort?: SortingRule[], nextToken?: string) => Promise<Portfolio[]>;
};

type Props = StateProps &
  DispatchProps & {
    user: User;
    show: boolean;
    onCancel: () => void;
    addPortfoliosToComparison: (portfolios: Portfolio[]) => Promise<void>;
  };

const LegacyLoadModelsModal = (props: Props) => {
  const {
    user,
    portfolios,
    myPortfolios,
    enterprisePortfolios,
    show,
    nextModelsToken,
    nextMyModelsToken,
    nextEnterpriseModelsToken,
    enterprise,
    isComparing,
    getModelsPortfolios,
    userPreferences,
  } = props;
  const [selectedPortfolios, setSelectedPortfolios] = useState<Portfolio[]>([]);
  const [sorting, setSorting] = useState<SortingRule[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const onSearch = (newQuery: string) => {
    setLoading(true);
    setQuery(newQuery);
  };

  const queryPortfolios = useDebouncedCallback((searchQuery: string, sortingRules: SortingRule[]) => {
    const promises: Promise<Portfolio[] | PaginatedPortfolios>[] = [];

    if (userHasPermissions(user, [PERMISSIONS.ALL_MODELS_LIST, PERMISSIONS.USER_MODELS, PERMISSIONS.ENTERPRISE_MODELS_LIST])) {
      promises.push(getModelsPortfolios(undefined, searchQuery, sortingRules));
    }

    if (userHasPermission(user, PERMISSIONS.USER_MODELS)) {
      // My Models
      promises.push(getModelsPortfolios(modelType.User, searchQuery, sortingRules));
    }

    if (userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST) && enterprise) {
      // Enterprise Models
      promises.push(getModelsPortfolios(modelType.Enterprise, searchQuery, sortingRules));
    }

    Promise.all(promises).then(() => {
      setLoading(false);
    });
  }, 300);

  useEffect(() => {
    queryPortfolios(query, sorting);
  }, [sorting, query, queryPortfolios]);

  if (!user) {
    return null;
  }

  const hasMoreModels = !!nextModelsToken;
  const hasMoreMyModels = !!nextMyModelsToken;
  const hasMoreEnterpriseModels = !!nextEnterpriseModelsToken;

  const onSelectPortfolios = (portfolios: Portfolio[]) => {
    const newPortfolios = selectedPortfolios.slice();
    setSelectedPortfolios(union(newPortfolios.slice(), portfolios));
  };

  const onUnselectPortfolios = (portfolios: Portfolio[]) => {
    const newPortfolios = selectedPortfolios.filter(p => !portfolios.includes(p));
    return setSelectedPortfolios(newPortfolios);
  };

  const onClose = () => {
    props.onCancel();
    setSelectedPortfolios([]);
  };

  const onLoadSelectedPortfolios = () => {
    selectedPortfolios.forEach(selectedPortfolio => {
      heapUtils.track(HEAP_CUSTOM_EVENTS.LOAD_MODEL, { isEnterpriseModel: isEnterpriseModel(selectedPortfolio) });
    });

    props.addPortfoliosToComparison(selectedPortfolios).then(() => {
      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);
      onClose();
    });
  };

  const loadMoreModelPortfolios = () => {
    getModelsPortfolios(undefined, query, sorting, nextModelsToken);
  };

  const loadMoreMyModelsPortfolios = () => {
    getModelsPortfolios(modelType.User, query, sorting, nextMyModelsToken);
  };

  const loadMoreEnterprisePortfolios = () => {
    getModelsPortfolios(modelType.Enterprise, query, sorting, nextEnterpriseModelsToken);
  };

  const getDisplayedColumns = () => {
    const { user } = props;

    return [
      {
        key: PORTFOLIO_TABLE_COLUMNS.NAME,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.RISK_PROFILE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
        hidden: !userHasPermission(user, PERMISSIONS.SHOW_PORTFOLIO_DETAILS),
      },
    ]
      .filter(col => !col.hidden)
      .map(col => col.key);
  };

  const onSort = (sort: SortingRule[]) => {
    setLoading(true);
    setSorting(sort);
  };

  const displayedColumns = getDisplayedColumns();
  const displayedMetrics = [
    PORTFOLIO_METRICS_FIELDS.MER,
    PORTFOLIO_METRICS_FIELDS.YEAR_ONE,
    PORTFOLIO_METRICS_FIELDS.YEAR_THREE,
    PORTFOLIO_METRICS_FIELDS.SIZE,
  ];

  const isThemeLight = userPreferences?.theme === ThemeMode.LIGHT;

  return (
    <Modal
      show={show}
      className={classNames('load-models-modal', { light: isThemeLight })}
      onHide={onClose}
      dialogClassName="modal-dialog-centered"
      size={MODAL_SIZES.LG}
    >
      <Modal.Body className="d-flex flex-column">
        <div className="modal-header">
          <FormattedMessage tagName="h1" id="ModelPortfoliosModal.Title" />
          <CloseButton onClick={onClose} />
        </div>

        <div className="content d-flex flex-column justify-content-center flex-grow-1">
          <ModelsPortfoliosList
            query={query}
            portfolios={portfolios}
            myPortfolios={myPortfolios}
            enterprisePortfolios={enterprisePortfolios}
            enterprise={enterprise}
            onSort={onSort}
            selectedPortfolios={selectedPortfolios}
            resetSelectedPortfolios={() => setSelectedPortfolios([])}
            loading={loading}
            hasMorePortfolios={hasMoreModels}
            hasMoreMyPortfolios={hasMoreMyModels}
            hasMoreEnterprisePortfolios={hasMoreEnterpriseModels}
            loadMorePortfolios={loadMoreModelPortfolios}
            loadMoreMyPortfolios={loadMoreMyModelsPortfolios}
            loadMoreEnterprisePortfolios={loadMoreEnterprisePortfolios}
            onSearchPortfolios={onSearch}
            emptyStateHeader={formatMessage('EmptyState.NoModelPortfolios.Title')}
            displayEnterpriseLabels
            hideStats={false}
            displayedColumns={displayedColumns}
            onPortfoliosSelected={onSelectPortfolios}
            onUnselectPortfolios={onUnselectPortfolios}
            displayedMetrics={displayedMetrics}
            selectedTitle="PortfoliosTable.SelectedTitle.Models"
            user={user}
            isThemeLight={isThemeLight}
          />
        </div>

        <div className="modal-actions d-flex justify-content-between">
          <SecondaryButton className="cancel-button" onClick={onClose} disabled={isComparing}>
            <FormattedMessage id="Common.Close" />
          </SecondaryButton>

          <PrimaryButton loading={isComparing} disabled={!selectedPortfolios.length} className="compare-button" onClick={onLoadSelectedPortfolios}>
            <FormattedMessage
              id={`ModelPortfoliosModal.${isComparing ? 'ComparingSelected' : !isThemeLight ? 'CompareSelected' : 'CompareSelection'}`}
              values={{ count: selectedPortfolios.length }}
            />
          </PrimaryButton>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  isComparing: selectIsLoadingEvery(state, [ADD_PORTFOLIOS_TO_COMPARISON.type]),
  portfolios: state.portfolios.models,
  myPortfolios: state.portfolios.myModels,
  enterprisePortfolios: state.portfolios.enterpriseModels,
  enterprise: state.enterprise.details,
  nextModelsToken: state.portfolios.nextModelsToken,
  nextMyModelsToken: state.portfolios.nextMyModelsToken,
  nextEnterpriseModelsToken: state.portfolios.nextEnterpriseModelsToken,
  userPreferences: state.auth.user?.preferences,
});

export default connect(mapStateToProps, {
  getModelsPortfolios,
})(LegacyLoadModelsModal);
